/**
 * Értékelés-API hibák felhasználói, magyar szöveggé alakítása.
 *
 * A DRF mezőhibáit (`{comment: ['…']}`) mezőnként adjuk vissza, hogy az űrlap a
 * mező mellé írhassa; a 429-es (túl sok kérés) válasz saját, barátságos üzenetet kap.
 * Nyers JSON soha nem jut a felhasználó elé (a formatApiError-ra támaszkodunk).
 */
import { formatApiError } from './apiError';

export const REVIEW_RATE_LIMIT_MESSAGE =
  'Túl sok értékelést küldtél rövid időn belül — próbáld újra később.';

/** Az űrlap mezői, amelyekhez a szerver mezőhibát adhat. */
export const REVIEW_FORM_FIELDS = ['rating', 'title', 'comment'] as const;
export type ReviewFormField = (typeof REVIEW_FORM_FIELDS)[number];

export interface ReviewError {
  /** Összefoglaló (űrlap feletti) üzenet — üres, ha minden hiba mezőhöz kötött. */
  message: string;
  fieldErrors: Partial<Record<ReviewFormField, string>>;
  status?: number;
}

interface ErrorWithResponse {
  response?: { status?: number; data?: unknown };
}

const firstMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const msg = firstMessage(item);
      if (msg) return msg;
    }
  }
  return null;
};

const FIELD_LABELS: Record<string, string> = {
  product: 'Termék',
  rating: 'Értékelés',
  title: 'Cím',
  comment: 'Vélemény',
  text: 'Válasz',
  reason: 'Indoklás',
};

export const toReviewError = (error: unknown): ReviewError => {
  const response = (typeof error === 'object' && error !== null ? (error as ErrorWithResponse) : {}).response;
  const status = response?.status;
  if (status === 429) {
    return { message: REVIEW_RATE_LIMIT_MESSAGE, fieldErrors: {}, status };
  }

  const fieldErrors: ReviewError['fieldErrors'] = {};
  const data = response?.data;
  let summary: string | null = null;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const body = data as Record<string, unknown>;
    REVIEW_FORM_FIELDS.forEach((field) => {
      const msg = firstMessage(body[field]);
      if (msg) fieldErrors[field] = msg;
    });
    // Mezőhöz nem köthető, de érthető üzenetek (pl. „Már értékelted ezt a terméket.").
    summary =
      firstMessage(body.product) ??
      firstMessage(body.non_field_errors) ??
      firstMessage(body.detail) ??
      firstMessage(body.error);
  }

  if (!summary && Object.keys(fieldErrors).length === 0) {
    summary = formatApiError(error, FIELD_LABELS);
  }
  return { message: summary ?? '', fieldErrors, status };
};

/** Egysoros üzenet (értesítéshez): összefoglaló, vagy az első mezőhiba. */
export const reviewErrorMessage = (error: unknown): string => {
  const parsed = toReviewError(error);
  return parsed.message || Object.values(parsed.fieldErrors)[0] || formatApiError(error, FIELD_LABELS);
};
